import { Controller } from "@hotwired/stimulus"

// Fallbacks for the server-side copy passed in through the `translations`
// value; a key the view did not send never reads as `undefined`.
const DEFAULT_TRANSLATIONS = {
  autoEnabledMessage: "",
  autoDisabledMessage: "",
  registrationRequiredMessage: ""
}

// Connects to data-controller="documents--reverse-charge"
export default class extends Controller {
  static targets = ["formula", "section", "toggle"]
  static outlets = ["documents--item", "documents--turnover"]
  static values = {
    translations: { type: Object, default: {} },
    reverseCharge: { type: Boolean, default: false },
    formula: { type: String, default: "" },
    accountCountryId: { type: String, default: "" },
    canUseReverseCharge: { type: Boolean, default: true }
  }

  get #texts() {
    return { ...DEFAULT_TRANSLATIONS, ...this.translationsValue }
  }

  connect() {
    // Listen for contact selection changes
    this._selectedContactListener = this.#onContactSelected.bind(this)
    document.addEventListener("selectedContact", this._selectedContactListener)
  }

  disconnect() {
    document.removeEventListener("selectedContact", this._selectedContactListener)
  }

  toggleReverseCharge(event = null) {
    // If called programmatically (no event), toggle the target state
    if (!event && this.hasToggleTarget) {
      this.toggleTarget.checked = !this.toggleTarget.checked
      this.reverseChargeValue = this.toggleTarget.checked
    } else if (event) {
      // If called from form action, use event target
      this.reverseChargeValue = event.target.checked
    }

    this.#updateReverseChargeState()
  }

  #updateReverseChargeState() {
    if (this.reverseChargeValue) {
      this.#setReverseChargeFormula()
    } else {
      this.#clearReverseChargeText()
    }

    this.#updateReverseChargeDisplay()
    this.#broadcastToItems()
    this.#broadcastToTurnover()
  }

  #updateReverseChargeDisplay() {
    if (this.hasSectionTarget) {
      this.sectionTarget.classList.toggle("d-none", !this.reverseChargeValue)
    }
  }

  #setReverseChargeFormula() {
    if (!this.hasFormulaTarget || !this.formulaValue) return

    // Only seed an EMPTY field. Toggling reverse charge — which selecting a
    // contact can do automatically — used to overwrite unconditionally, so an
    // imported document's own reverse-charge wording, or anything the user had
    // typed, was replaced by our synthesised text. That wording is the legal
    // record on an imported invoice; the server side already refuses to
    // overwrite it, and this is the client-side half of the same rule.
    if (this.formulaTarget.value.trim() !== "") return

    this.formulaTarget.value = this.formulaValue
  }

  #clearReverseChargeText() {
    if (this.hasFormulaTarget) {
      this.formulaTarget.value = ""
    }
  }

  #broadcastToTurnover() {
    // Call public API on all connected turnover outlets
    if (!this.hasDocumentsTurnoverOutlet) return

    if (this.reverseChargeValue) {
      this.documentsTurnoverOutlets.forEach((outlet) => outlet.disableTurnover())
    } else {
      this.documentsTurnoverOutlets.forEach((outlet) => outlet.enableTurnover())
    }
  }

  #broadcastToItems() {
    // Call public API on all connected invoice item outlets
    if (!this.hasDocumentsItemOutlet) return
    if (this.reverseChargeValue) {
      this.documentsItemOutlets.forEach((outlet) => outlet.enableReverseCharge())
    } else {
      this.documentsItemOutlets.forEach((outlet) => outlet.disableReverseCharge())
    }
  }

  #onContactSelected(event) {
    const clientData = event.detail

    // Check if client is international
    // Convert both values to strings for consistent comparison, handling null/undefined
    const clientCountryId = clientData.country_id ? String(clientData.country_id) : ""
    const accountCountryId = this.accountCountryIdValue ? String(this.accountCountryIdValue) : ""
    const isInternational = clientCountryId && accountCountryId && clientCountryId !== accountCountryId

    if (isInternational && !this.reverseChargeValue) {
      // Only auto-enable if user can use reverse charge
      if (this.canUseReverseChargeValue) {
        this.toggleReverseCharge()
        this.#showNotification(this.#texts.autoEnabledMessage)
      } else {
        // Show registration requirement notification for users who cannot use reverse charge
        this.#showNotification(this.#texts.registrationRequiredMessage)
      }
    } else if (!isInternational && this.reverseChargeValue) {
      this.toggleReverseCharge()
      this.#showNotification(this.#texts.autoDisabledMessage)
    }
  }

  #showNotification(message) {
    if (!message) return

    // Create a Bootstrap toast element with structure similar to flash_renderer
    const toastElement = document.createElement("div")
    toastElement.className = "toast"
    toastElement.setAttribute("role", "alert")
    toastElement.setAttribute("aria-live", "assertive")
    toastElement.setAttribute("aria-atomic", "true")
    toastElement.setAttribute("data-controller", "toast")
    toastElement.innerHTML = `
            <div class="toast-header text-white">
                <span class="material-symbols-outlined">info</span>
                <strong class="me-auto">${message}</strong>
                <button type="button" class="btn-close btn-close-white" data-bs-dismiss="toast" aria-label="Close"></button>
            </div>
        `

    // Add to toast container or create one
    let toastContainer = document.getElementById("toast-container")
    if (!toastContainer) {
      toastContainer = document.createElement("div")
      toastContainer.id = "toast-container"
      toastContainer.className = "toast-container"
      document.body.appendChild(toastContainer)
    }

    toastContainer.appendChild(toastElement)
  }
}
